// frontend/src/pages/Admin/Billing.jsx
import { useState, useEffect } from 'react';
import { getAdminBilling } from '../../api/services';

// Map raw product_type enums to human-readable labels for display.
const PRODUCT_LABELS = {
    essential_monthly: 'Essential (Monthly)',
    professional_monthly: 'Professional (Monthly)',
    expert_monthly: 'Expert (Monthly)',
    power_monthly: 'Power (Monthly)',
    payg_diagnose: 'PAYG · Analysis',
    payg_rewrite: 'PAYG · Rewrite',
};

const formatProduct = (raw) =>
    PRODUCT_LABELS[raw] ||
    // Fallback: turn any unmapped enum into Title Case words.
    (raw ? raw.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '—');

const formatDate = (iso) =>
    new Date(iso).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });

export default function AdminBilling() {
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchBillingRecords();
    }, []);

    const fetchBillingRecords = async () => {
        setError(null);
        try {
            const { data } = await getAdminBilling({ limit: 50 });
            setRecords(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Failed to fetch billing records:', err);
            setError('Could not load billing records. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <svg className="animate-spin h-10 w-10 text-brand-amber" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                    <path className="opacity-80" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
            </div>
        );
    }

    return (
        <div>
            <h1 className="font-display text-2xl font-bold text-brand-text mb-6">Billing Records</h1>

            {error ? (
                <div className="card border-red-500/30 bg-red-500/5">
                    <div className="flex items-center justify-between gap-4">
                        <p className="text-red-400 font-body text-sm">{error}</p>
                        <button onClick={fetchBillingRecords} className="btn-ghost text-sm py-2 px-4 shrink-0">
                            Try again
                        </button>
                    </div>
                </div>
            ) : (
                <div className="card p-0 overflow-hidden">
                    <table className="min-w-full">
                        <thead>
                            <tr className="border-b border-brand-border">
                                <th className="px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase tracking-widest font-body">Amount</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase tracking-widest font-body">Currency</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase tracking-widest font-body">Product</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase tracking-widest font-body">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase tracking-widest font-body">Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {records.map((record) => (
                                <tr key={record.id} className="border-b border-brand-border last:border-0 hover:bg-brand-surface/50 transition-colors">
                                    <td className="px-6 py-4 text-sm font-medium text-brand-text font-body">£{record.amount}</td>
                                    <td className="px-6 py-4 text-sm text-brand-muted font-body">{(record.currency || '').toUpperCase()}</td>
                                    <td className="px-6 py-4 text-sm text-brand-text font-body">{formatProduct(record.product_type)}</td>
                                    <td className="px-6 py-4">
                                        <span className={`px-2.5 py-1 text-xs rounded-full font-body ${record.success_status ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                                            {record.success_status ? 'Success' : 'Failed'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-brand-muted font-body">{formatDate(record.created_at)}</td>
                                </tr>
                            ))}
                            {records.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="px-6 py-10 text-center text-brand-muted font-body text-sm">
                                        No billing records yet.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}